import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { IconPackId } from '@ukrop-nm/shared';
import { ACCOUNT_CHANGED_EVENT, SETTINGS_CHANGED_EVENT } from '../auth/accountScope';
import { loadUserUiSettings, saveUserUiSettings } from '../auth/userSettings';

interface IconPackContextValue {
  pack: IconPackId;
  setPack: (pack: IconPackId) => void;
}

const IconPackContext = createContext<IconPackContextValue | null>(null);

export function IconPackProvider({ children }: { children: ReactNode }) {
  const [pack, setPackState] = useState<IconPackId>(() => loadUserUiSettings().iconPack);

  useEffect(() => {
    const refresh = () => setPackState(loadUserUiSettings().iconPack);
    window.addEventListener(ACCOUNT_CHANGED_EVENT, refresh);
    window.addEventListener(SETTINGS_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(ACCOUNT_CHANGED_EVENT, refresh);
      window.removeEventListener(SETTINGS_CHANGED_EVENT, refresh);
    };
  }, []);

  const value = useMemo(
    () => ({
      pack,
      setPack: (next: IconPackId) => {
        setPackState(next);
        saveUserUiSettings({ iconPack: next });
      },
    }),
    [pack],
  );

  return <IconPackContext.Provider value={value}>{children}</IconPackContext.Provider>;
}

export function useIconPack() {
  const ctx = useContext(IconPackContext);
  if (!ctx) throw new Error('useIconPack must be used within IconPackProvider');
  return ctx;
}
